import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const useRcProRateTablesFunctions = () => {
  const [table, setTable] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [constants, setConstants] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${BASE_URL}/offers/rcpro/rate-table`, { withCredentials: true });
      const data = resp.data?.data;
      setTable(data);
      // Deep clone so edits don't mutate the original reference
      setSpecializations((data?.specializations || []).map(s => ({
        ...s,
        rates: { ...s.rates },
      })));
      setConstants({ ...(data?.constants || {}) });
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load RC Pro rate table');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRate = useCallback((specIndex, rateKey, value) => {
    setSpecializations(prev => {
      const next = [...prev];
      next[specIndex] = { ...next[specIndex], rates: { ...next[specIndex].rates, [rateKey]: value } };
      return next;
    });
    setDirty(true);
  }, []);

  const updateConstant = useCallback((field, value) => {
    setConstants(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      // Coerce all rate cells to numbers before sending
      const specsToSend = specializations.map(s => ({
        code: s.code,
        nogaCode: s.nogaCode,
        label: s.label,
        rates: Object.fromEntries(Object.entries(s.rates).map(([k, v]) => [k, Number(v)])),
      }));
      const constantsToSend = {
        stampDutyRate: constants.stampDutyRate != null && constants.stampDutyRate > 1
          ? Number(constants.stampDutyRate) / 100
          : Number(constants.stampDutyRate),
        coverageBasis: constants.coverageBasis,
        currency: constants.currency,
      };
      const resp = await axios.put(
        `${BASE_URL}/offers/rcpro/rate-table`,
        { specializations: specsToSend, constants: constantsToSend },
        { withCredentials: true },
      );
      const data = resp.data?.data;
      setTable(data);
      setSpecializations((data?.specializations || []).map(s => ({ ...s, rates: { ...s.rates } })));
      setConstants({ ...(data?.constants || {}) });
      setDirty(false);
      setToast('RC Pro rate table saved');
      setTimeout(() => setToast(''), 2400);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save RC Pro rate table');
    } finally {
      setSaving(false);
    }
  }, [specializations, constants]);

  return {
    table,
    specializations,
    constants,
    updateRate, updateConstant,
    loading, saving, dirty, error, toast,
    save,
    reload: load,
  };
};

export default useRcProRateTablesFunctions;
